import { Global } from '@emotion/core'
import { ThemeProvider } from 'emotion-theming'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Image } from 'rebass'
import AretaeioComingSoon from '../customPlugins/AretaeioComingSoon/AretaeioComingSoon'
import LoadingSpinner from '../customPlugins/LoadingSpinner/LoadingSpinner'
import FatalError from '../customPlugins/MayaErrors/FatalError'
import { ResponsibilityDisclaimerOverlay } from '../customPlugins/ResponsibilityDisclaimerOverlay/ResponsibilityDisclaimerOverlay'
import { useApplicationState } from '../hooks/useApplicationState'
import { useAutoResize } from '../hooks/useAutoResize'
import { useScrollToTop } from '../hooks/useScrollToTop'
import { endSession as endMayaSession } from '../socket'
import theme from '../theme'
import { AccountSetup } from './AccountSetup'
import { SetupCompleted } from './AccountSetup/SetupCompleted'
import { Authentication } from './Authentication'
import DigitalHuman from './DigitalHuman'
import Home from './Home'
import { DomainConfigProvider } from './contexts/DomainConfigContext'
import { SessionProvider } from './contexts/SessionContext'
import { useDomainConfigContext } from './hooks/useDomainConfigContext'
import useIsBlacklisted from './hooks/useIsBlacklisted'

import SessionTimedOut from './SessionTimedOut'
import { StorageKeys } from './types/StorageKeys'

export type Steps =
  | 'digitalHuman'
  | 'timed-out'
  | 'home'
  | 'login'
  | 'accountSetup'
  | 'accountSetupEdit'
  | 'setupCompleted'
  | 'editCompleted'
  | 'patientChooser'
  | 'patientStart'

export type LoginRedirectSteps = 'home' | 'patientStart'

const App = () => {
  const queryParams = new URLSearchParams(window.location.search)
  const embeddedApp = Boolean(queryParams.get('embedded'))
  // param to skip the blacklist check
  const noBlackList = Boolean(queryParams.get('noBL'))
  // param to always show buttons (skip the "Wait for Maya to finish speaking to show buttons")
  const showButtons = Boolean(queryParams.get('showButtons'))
  const timeoutTestTime = Number(queryParams.get('test_timeout_time'))
  const id = queryParams.get('id')
  // @ts-ignore
  const { t } = useTranslation()
  const {
    state: { appIncludedSteps, showLogoOnFlow },
    dispatch
  } = useDomainConfigContext()

  const [speak, setSpeak] = useState(true)
  const {
    currentLanguage,
    disableDH,
    disableLandscapeMode,
    personaId,
    headingMessage,
    initError,
    logo,
    supportedLanguages,
    activeTheme,
    bgChatImage,
    authLogo,
    setAuthLogo,
    bgImage,
    bgMobileImage,
    hideTranscript,
    hideDownloadNotes,
    loading,
    responsibilityDisclaimer,
    shouldAcceptTerms,
    userPhone,
    setUserPhone,
    userData,
    setUserData,
    setSid,
    sid,
    setPatientSelected,
    patientSelected,
    disableCB,
    phoneLoginDefaultString,
    initialStep,
    loadingMode,
    registeredUserLoginRedirectionStep
  } = useApplicationState({ embeddedApp })

  // For faster testing, skip straight to digitalHuman
  const [step, goTo] = useState<Steps>(
    appIncludedSteps.includes(initialStep || 'login')
      ? initialStep || 'login'
      : 'home'
  )

  useEffect(() => {
    // if we are always showing buttons (override behavior), save it in state
    if (showButtons) {
      dispatch({
        type: 'SET_SHOW_BUTTONS',
        payload: true
      })
    }
  }, [dispatch, showButtons])

  useEffect(() => {
    const now = new Date()
    const userFromLS = localStorage.getItem(StorageKeys.API_USER_DATA)

    // if the item doesn't exist, return null
    if (!userFromLS) {
      // if there's a patientId for Discharge, do nothing as we handle
      // it in Authentication.tsx
      if (id) {
        return null
      } else {
        // if there's no patientId, redirect to initialStep
        return initialStep && goTo(initialStep)
      }
    }

    if (userFromLS) {
      const userFromLSParsed = JSON.parse(userFromLS)

      // compare the expiry time of the item with the current time
      if (now.getTime() > userFromLSParsed.expiry) {
        // If the item is expired, delete the item from storage
        // and return null
        localStorage.removeItem(StorageKeys.API_USER_DATA)
        return null
      } else {
        setUserData(userFromLSParsed.value)
        goTo('home')
      }
    }
  }, [id, initialStep, setUserData])

  const restart = () => {
    endMayaSession()
    goTo(registeredUserLoginRedirectionStep || 'home')
  }

  const redirectAfterLogin = useCallback(
    (id: string) =>
      goTo(id ? registeredUserLoginRedirectionStep : 'patientChooser'),
    [registeredUserLoginRedirectionStep]
  )

  const stepIsDigitalHuman = useCallback(() => step === 'digitalHuman', [step])

  const goToPatientChooser = () => goTo('patientChooser')

  const height = useAutoResize({ condition: stepIsDigitalHuman })
  useScrollToTop({ condition: stepIsDigitalHuman })

  const finalTheme = theme(activeTheme, embeddedApp)

  const isBlacklisted = useIsBlacklisted(noBlackList)

  const startSession = (speak: boolean) => {
    setSpeak(speak)
    goTo('digitalHuman')
  }

  const redirAfterLogin = useCallback(() => redirectAfterLogin(id), [
    id,
    redirectAfterLogin
  ])

  if (isBlacklisted && !noBlackList) {
    return (
      <ThemeProvider theme={finalTheme}>
        <AretaeioComingSoon
          logo={logo}
          languageSelected={currentLanguage}
          supportedLanguages={supportedLanguages}
        />
      </ThemeProvider>
    )
  }

  if (loading) {
    return (
      <ThemeProvider theme={finalTheme}>
        <Box variant="questionsLoadingOverlay">
          <LoadingSpinner />
        </Box>
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={finalTheme}>
      {responsibilityDisclaimer && (
        <ResponsibilityDisclaimerOverlay
          disclaimerImage={
            responsibilityDisclaimer.responsibilityDisclaimerImage
          }
          disclaimerText={responsibilityDisclaimer.responsibilityDisclaimerText}
        />
      )}
      <Global styles={theme => ({ body: { fontFamily: theme.fonts.body } })} />
      <Box
        variant="mainAppContainer"
        sx={{
          height: height || '100%',
          overflow: step === 'home' ? 'visible' : 'hidden',
          ...(bgImage && (step === 'home' || step === 'patientChooser')
            ? finalTheme.appStyles.appBackground(bgImage, bgMobileImage)
            : {}),
          ...(bgChatImage && (step === 'digitalHuman' || step === 'timed-out')
            ? finalTheme.appStyles.chatBackground(bgChatImage)
            : {})
        }}
      >
        {showLogoOnFlow && (
          <Box variant="homeLogo">
            <Image src={logo} alt="Logo"></Image>
          </Box>
        )}
        {initError && (
          <FatalError
            errorTitle={t('Error.serverError')}
            errorMessage={initError}
            clearError={() => window.location.reload()}
            buttonText={t('Error.tryAgain')}
          />
        )}
        {appIncludedSteps.includes('digitalHuman') &&
        step === 'digitalHuman' ? (
          <DigitalHuman
            speak={speak}
            onTimedOut={() => goTo('timed-out')}
            restart={restart}
            onSessionEnded={() => {}}
            embeddedMode={embeddedApp}
            personaId={personaId}
            testTimeoutTime={timeoutTestTime}
            hideTranscript={hideTranscript}
            hideDownloadNotes={hideDownloadNotes}
            session={sid}
            patientSelectedId={patientSelected?._id}
            preAuthToken={userData && userData[0].preAuthToken}
            loadingMode={loadingMode}
            disableLandscapeMode={disableLandscapeMode}
            // TODO: change to be a specific config from the chatbot
            digitalHumanOnly={
              registeredUserLoginRedirectionStep === 'patientStart'
            }
          />
        ) : appIncludedSteps.includes('timed-out') && step === 'timed-out' ? (
          <SessionTimedOut restart={restart} />
        ) : (appIncludedSteps.includes('home') && step === 'home') ||
          (appIncludedSteps.includes('patientChooser') &&
            step === 'patientChooser') ? (
          <Home
            embeddedMode={embeddedApp}
            startSession={startSession}
            restart={() => goTo('home')}
            headingMessage={headingMessage}
            disableDigitalHuman={disableDH}
            disableChatBot={disableCB}
            logo={logo}
            languageSelected={currentLanguage}
            supportedLanguages={supportedLanguages}
            showVideoBackground={bgImage === null}
            shouldAcceptTerms={shouldAcceptTerms}
            patientSelector={appIncludedSteps.includes('accountSetup')}
            userData={userData}
            setSid={setSid}
            setPatientSelected={setPatientSelected}
            patientSelected={patientSelected}
            onManageFamilyMembers={() => goTo('accountSetupEdit')}
          />
        ) : (appIncludedSteps.includes('setupCompleted') &&
            step === 'setupCompleted') ||
          (appIncludedSteps.includes('editCompleted') &&
            step === 'editCompleted') ? (
          <SetupCompleted
            onSuccess={() =>
              goTo(step === 'editCompleted' ? 'patientChooser' : 'home')
            }
          />
        ) : (appIncludedSteps.includes('accountSetup') &&
            step === 'accountSetup') ||
          (appIncludedSteps.includes('accountSetupEdit') &&
            step === 'accountSetupEdit') ? (
          <AccountSetup
            userData={userData}
            setUserData={setUserData}
            userPhone={userPhone}
            onSuccess={() =>
              goTo(
                step === 'accountSetupEdit' ? 'editCompleted' : 'setupCompleted'
              )
            }
            onBackButton={
              appIncludedSteps.includes('accountSetupEdit') &&
              step === 'accountSetupEdit'
                ? goToPatientChooser
                : undefined
            }
          />
        ) : appIncludedSteps.includes('login') &&
          (step === 'login' || step === 'patientStart') ? (
          <Authentication
            domainConfigLoading={loading}
            authLogo={authLogo}
            setAuthLogo={setAuthLogo}
            appStep={step}
            startSession={startSession}
            onRegisteredUserLogin={redirAfterLogin}
            onUnregisteredUserLogin={() => goTo('accountSetup')}
            supportedLanguages={supportedLanguages}
            languageSelected={currentLanguage}
            setUserPhone={setUserPhone}
            setUserData={setUserData}
            phoneLoginDefaultString={phoneLoginDefaultString}
          />
        ) : null}
      </Box>
    </ThemeProvider>
  )
}

const AppWrapper = () => {
  return (
    <DomainConfigProvider>
      <SessionProvider>
        <App />
      </SessionProvider>
    </DomainConfigProvider>
  )
}

export default AppWrapper
